import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Copy, X, LogOut, Loader2, Pencil, Check } from "lucide-react";

function CopyLink({ link }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };
  return (
    <div className="mt-3 flex items-center gap-2 border border-dashed border-ember/40 bg-ember/5 px-3 py-2" data-testid="invite-link-row">
      <a href={link} target="_blank" rel="noreferrer" className="flex-1 font-mono text-[11px] text-sand-600 truncate hover:text-ember hover:underline">{link}</a>
      <button onClick={copy} className="shrink-0 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-ember hover:underline" data-testid="invite-link-copy">
        <Copy size={11} /> Copy
      </button>
    </div>
  );
}

function MemberRow({ m, isOwner, onRemove }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-sand-100" data-testid={`family-member-${m.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">
          {m.name || m.email}
          {m.is_me && <span className="ml-2 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-sand-100 text-sand-600">you</span>}
          {m.is_owner && <span className="ml-2 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-ember text-white">owner</span>}
        </p>
        <p className="text-[11px] font-mono text-sand-500 truncate">{m.email}</p>
      </div>
      {isOwner && !m.is_me && (
        <button onClick={() => onRemove(m)} className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-error" data-testid={`family-remove-${m.id}`}>
          Remove
        </button>
      )}
    </div>
  );
}

export default function FamilySection() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const load = () => {
    api.get("/family").then(({ data }) => setData(data)).catch(() => toast.error("Could not load family"));
  };
  useEffect(load, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/family/invites", { email: email.trim() });
      setLastLink(res.invite_link);
      toast.success(res.email_sent ? `Invite emailed to ${email.trim()}` : "Invite created — share the link below");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/family/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch { toast.error("Could not revoke invite"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from the family? They keep the trips they created.`)) return;
    try {
      await api.delete(`/family/members/${m.id}`);
      toast.success("Member removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove member"); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this family? You'll keep the trips you created but lose access to the rest.")) return;
    try {
      await api.post("/family/leave");
      toast.success("You left the family");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not leave"); }
  };

  const saveName = async () => {
    try {
      await api.patch("/family", { name: nameDraft.trim() });
      setEditingName(false);
      toast.success("Family renamed");
      load();
    } catch { toast.error("Could not rename"); }
  };

  if (!data) return <section className="bg-surface rounded-card border border-hairline p-6 md:p-8 mt-6 h-48 animate-pulse" data-testid="family-section-loading" />;

  const { family, members, invites, is_owner } = data;

  return (
    <section className="bg-surface rounded-card border border-hairline p-6 md:p-8 mt-6" data-testid="family-section">
      <div className="flex items-center gap-2 mb-1">
        <Users size={15} className="text-ember" />
        <h2 className="font-display font-medium text-xl tracking-tight">Family</h2>
      </div>
      <p className="text-xs text-sand-500 mb-4 max-w-md">
        Everyone in the family sees and manages the same trips, budget, wishlist and plans.
        Act 60 tracking, travel profile and AI chats stay personal.
      </p>

      <div className="flex items-center gap-2 mb-5">
        {editingName ? (
          <>
            <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} maxLength={80}
              className="border border-sand-300 px-2 py-1 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="family-name-input" autoFocus onKeyDown={(e) => e.key === "Enter" && saveName()} />
            <button onClick={saveName} className="text-ember" data-testid="family-name-save"><Check size={14} /></button>
            <button onClick={() => setEditingName(false)} className="text-sand-400"><X size={14} /></button>
          </>
        ) : (
          <>
            <p className="font-mono text-sm font-semibold" data-testid="family-name">{family.name}</p>
            <button onClick={() => { setNameDraft(family.name); setEditingName(true); }}
              className="text-sand-400 hover:text-ember" data-testid="family-name-edit"><Pencil size={12} /></button>
          </>
        )}
      </div>

      <div className="mb-6">
        <p className="label-tag mb-1">Members ({members.length})</p>
        {members.map((m) => <MemberRow key={m.id} m={m} isOwner={is_owner} onRemove={remove} />)}
        {!is_owner && members.length > 1 && (
          <button onClick={leave} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-sand-500 hover:text-error" data-testid="family-leave">
            <LogOut size={11} /> Leave family
          </button>
        )}
      </div>

      <form onSubmit={invite} className="border-t border-sand-100 pt-5">
        <p className="label-tag mb-2">Invite someone</p>
        <div className="flex gap-2">
          <input type="email" required placeholder="family@member.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="flex-1 border border-sand-300 px-3 py-2 text-sm focus:outline-none focus:border-ember"
            data-testid="family-invite-email" />
          <button type="submit" disabled={busy} className="btn-swiss btn-primary text-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="family-invite-send">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Invite
          </button>
        </div>
        {lastLink && <CopyLink link={lastLink} />}
      </form>

      {invites.length > 0 && (
        <div className="mt-5">
          <p className="label-tag mb-1">Pending invites</p>
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-3 py-2 border-b border-sand-100" data-testid={`family-invite-${inv.id}`}>
              <div className="min-w-0">
                <p className="text-sm truncate">{inv.email}</p>
                <p className="text-[10px] font-mono text-sand-400">expires {String(inv.expires_at).slice(0, 10)}</p>
              </div>
              <button onClick={() => revoke(inv.id)} className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-error" data-testid={`family-invite-revoke-${inv.id}`}>
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
